import React, { useState, useCallback, useEffect, useRef } from "react";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import { fetchTelegramPaymentData, fetchTelegramPaymentSummary } from "../../services/payment-service.js";
import companyService from "../../services/company-service.js";
import DoubleCalendar from "../../components/comman/dataPicker.js";
import Modal from "../../components/comman/modal.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import ErrorDisplay from "../../components/comman/ErrorDisplay.js";
import CustomMultiSelect from "../../components/comman/multi-select.js";
import RowsPerPageSelect from "../../components/comman/RowsPerPageSelect.js";
import Pagination from "../../components/comman/pagination.js";
import Button from "../../components/comman/button.js";
import Toast from "../../components/Toast/toast.js";
import calendar from "../../assets/calendar.svg";

const TelegramPayments = () => {
    const { language } = useLanguage();
    const toastRef = useRef(null);
    
    // Date states
    const today = new Date();
    const [dateFrom, setDateFrom] = useState(today);
    const [dateTo, setDateTo] = useState(today);
    const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
    
    // Filter states
    const [statusFilter, setStatusFilter] = useState([]);
    const [companyFilter, setCompanyFilter] = useState([]);
    const [companies, setCompanies] = useState([]);
    
    // Data states
    const [summaryData, setSummaryData] = useState([]);
    const [orderData, setOrderData] = useState({ content: [], totalElements: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    const formatDate = (date) => {
        if (!date) return null;
        return date.toLocaleDateString('fr-CA'); // YYYY-MM-DD
    };

    // Quick date picker actions
    const setToday = () => {
        const d = new Date();
        setDateFrom(d);
        setDateTo(d);
    };

    const setYesterday = () => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        setDateFrom(d);
        setDateTo(d);
    };

    const setThisWeek = () => {
        const d = new Date();
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
        const start = new Date(d.setDate(diff));
        setDateFrom(start);
        setDateTo(new Date());
    };

    const setThisMonth = () => {
        const d = new Date();
        const start = new Date(d.getFullYear(), d.getMonth(), 1);
        setDateFrom(start);
        setDateTo(new Date());
    };

    // Fetch initial data (companies)
    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const data = await companyService.getCompanies();
                setCompanies(data.map(c => ({ value: c.id, label: c.name })));
            } catch (err) {
                console.error("Failed to fetch companies:", err);
            }
        };
        fetchCompanies();
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                dateFrom: formatDate(dateFrom),
                dateTo: formatDate(dateTo),
                status: statusFilter.map(s => s.value).join(','),
                limit: itemsPerPage,
                offset: (currentPage - 1) * itemsPerPage
            };

            const summaryParams = {
                dateFrom: formatDate(dateFrom),
                dateTo: formatDate(dateTo),
                status: statusFilter.map(s => s.value).join(',')
            };

            // If company filter is set, it only applies to detailed data per requirement usually, 
            // but we'll check if summary also needs it. Summary endpoint doesn't take companyId.
            const detailedParams = { ...params };
            if (companyFilter.length > 0) {
                detailedParams.companyId = companyFilter[0].value; // Single company for now as per endpoint signature
            }

            const [summary, detailed] = await Promise.all([
                fetchTelegramPaymentSummary(summaryParams),
                fetchTelegramPaymentData(detailedParams)
            ]);

            setSummaryData(summary || []);
            setOrderData({
                content: detailed?.orders || [],
                totalElements: detailed?.totalCount || 0
            });
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo, statusFilter, companyFilter, currentPage, itemsPerPage]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleDateSelection = (selectedDates) => {
        if (selectedDates && selectedDates.length > 0) {
            setDateFrom(new Date(selectedDates[0]));
            setDateTo(new Date(selectedDates[selectedDates.length - 1]));
            setIsDatePickerVisible(false);
        }
    };

    const statusOptions = [
        { value: "PAYED", label: getTranslation("paymentStatuses.payed", language) },
        { value: "NOT_PAYED", label: getTranslation("paymentStatuses.not_payed", language) },
        { value: "PENDING", label: getTranslation("paymentStatuses.pending", language) },
        { value: "REFUNDED", label: getTranslation("paymentStatuses.refunded", language) },
        { value: "CANCELLED", label: getTranslation("paymentStatuses.cancelled", language) },
        { value: "FAILED", label: getTranslation("paymentStatuses.failed", language) }
    ];

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            {/* Quick Date Picks */}
            <div className="flex flex-wrap gap-2 mb-6">
                <Button variant={formatDate(dateFrom) === formatDate(today) && formatDate(dateTo) === formatDate(today) ? "primary" : "secondary"} size="sm" onClick={setToday}>
                    {getTranslation("common.today", language) || "Today"}
                </Button>
                <Button variant="secondary" size="sm" onClick={setYesterday}>
                    {getTranslation("common.yesterday", language) || "Yesterday"}
                </Button>
                <Button variant="secondary" size="sm" onClick={setThisWeek}>
                    {getTranslation("common.thisWeek", language) || "This Week"}
                </Button>
                <Button variant="secondary" size="sm" onClick={setThisMonth}>
                    {getTranslation("common.thisMonth", language) || "This Month"}
                </Button>
                <div 
                    className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-[#161b22] border border-gray-300 dark:border-[#30363d] rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1c2128]"
                    onClick={() => setIsDatePickerVisible(true)}
                >
                    <img src={calendar} alt="calendar" className="w-4 h-4" />
                    <span className="text-sm font-medium text-gray-700 dark:text-[#c9d1d9]">
                        {formatDate(dateFrom)} - {formatDate(dateTo)}
                    </span>
                </div>
            </div>

            {/* Summary Section */}
            <div className="mb-8">
                <h2 className="text-lg font-semibold mb-4 text-gray-800 dark:text-[#c9d1d9]">
                    {getTranslation("telegramPayments.summary", language)}
                </h2>
                <div className="overflow-x-auto bg-white dark:bg-[#161b22] rounded-xl border border-gray-200 dark:border-[#30363d] shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-[#30363d]">
                        <thead className="bg-gray-50 dark:bg-[#0d1117]">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">
                                    {getTranslation("telegramPayments.company", language)}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">
                                    {getTranslation("telegramPayments.deliveredOrders", language)}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">
                                    {getTranslation("telegramPayments.totalEarnings", language)}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">
                                    {getTranslation("telegramPayments.paidOrders", language)}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">
                                    {getTranslation("telegramPayments.unpaidOrders", language)}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-[#30363d]">
                            {summaryData.length > 0 ? (
                                summaryData.map((summary, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-[#1c2128] transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-[#c9d1d9]">
                                            {summary.company?.name || "No Company"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-[#8b949e]">
                                            {summary.deliveredOrders?.count || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-[#8b949e]">
                                            {parseFloat(summary.deliveredOrders?.sumPrice || 0).toLocaleString()} ֏
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400 font-medium">
                                            {summary.deliveredOrders?.payedTotal || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400 font-medium">
                                            {summary.deliveredOrders?.notPayedTotal || 0}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-10 text-center text-gray-500 dark:text-[#8b949e]">
                                        {loading ? <LoadingSpinner /> : "No data available"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Filters for Detailed Data */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="w-full sm:max-w-xs z-30">
                    <CustomMultiSelect
                        label={getTranslation("orderPage.status", language) || "Status"}
                        options={statusOptions}
                        value={statusFilter}
                        onChange={(selected) => {
                            setStatusFilter(selected);
                            setCurrentPage(1);
                        }}
                        placeholder={getTranslation("orderStatuses.all", language) || "All statuses"}
                    />
                </div>
                <div className="w-full sm:max-w-xs z-20">
                    <CustomMultiSelect
                        label={getTranslation("telegramPayments.company", language) || "Company"}
                        options={companies}
                        value={companyFilter}
                        onChange={(selected) => {
                            setCompanyFilter(selected);
                            setCurrentPage(1);
                        }}
                        placeholder={getTranslation("orderPage.allCompanies", language) || "All Companies"}
                    />
                </div>
            </div>

            {/* Detailed Order Table */}
            <div className="bg-white dark:bg-[#161b22] rounded-xl border border-gray-200 dark:border-[#30363d] shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-[#30363d]">
                        <thead className="bg-gray-50 dark:bg-[#0d1117]">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">
                                    {getTranslation("telegramPayments.customer", language)}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">
                                    {getTranslation("telegramPayments.company", language)}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">
                                    {getTranslation("orderPage.status", language)}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">
                                    {getTranslation("telegramPayments.price", language)}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">
                                    {getTranslation("common.date", language)}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-[#30363d]">
                            {orderData.content.length > 0 ? (
                                orderData.content.map((order) => (
                                    <tr key={order.orderId} className="hover:bg-gray-50 dark:hover:bg-[#1c2128] transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-[#c9d1d9]">
                                            <a
                                                href={`https://kms.bychef.am/orders/${order.orderId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 dark:text-[#58a6ff] hover:underline font-medium"
                                            >
                                                #{order.orderId}
                                            </a>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 dark:text-[#c9d1d9]">
                                                {order.user?.fullName || "Unknown"}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-[#8b949e]">
                                                {order.user?.phoneNumber || ""}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-[#8b949e]">
                                            {order.company?.name || "Personal"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                                ${order.paymentStatus === 'PAYED' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {getTranslation(`paymentStatuses.${order.paymentStatus.toLowerCase()}`, language)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-[#c9d1d9] font-medium">
                                            {(order.orderTotalPrice || 0).toLocaleString()} ֏
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-[#8b949e]">
                                            {new Date(order.orderDate).toLocaleDateString(language === 'am' ? 'hy-AM' : language)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-6 py-10 text-center text-gray-500 dark:text-[#8b949e]">
                                        {loading ? <LoadingSpinner /> : "No individual orders found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {orderData.totalElements > 0 && (
                    <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-[#30363d]">
                        <div className="flex items-center gap-4">
                            <RowsPerPageSelect
                                label={
                                    getTranslation("common.rowsPerPage", language) ||
                                    "Rows per page"
                                }
                                value={itemsPerPage}
                                onChange={(value) => {
                                    setItemsPerPage(value);
                                    setCurrentPage(1);
                                }}
                                margin=""
                                menuPlacement="top"
                            />
                            <span className="text-sm text-gray-500 dark:text-[#8b949e]">
                                {getTranslation("common.total", language)}: {orderData.totalElements}
                            </span>
                        </div>
                        <Pagination
                            totalItems={orderData.totalElements}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}
            </div>

            {/* Error Display */}
            {error && <ErrorDisplay error={error} onRetry={loadData} />}

            {/* Date Picker Modal */}
            <Modal
                isOpen={isDatePickerVisible}
                onClose={() => setIsDatePickerVisible(false)} 
                title={getTranslation("payments.selectDate", language)}
                size="4xl"
            >
                <DoubleCalendar 
                    onClose={() => setIsDatePickerVisible(false)}
                    onDateSelect={handleDateSelection}
                    initialDateFrom={dateFrom}
                    initialDateTo={dateTo}
                />
            </Modal>

            <Toast ref={toastRef} />
        </div>
    );
};

export default TelegramPayments;
